import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { childrenInfoService } from '../../../../services/childrenInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('ChildrenInfoInput');

type ChildrenInfoInputScreenRouteProp = RouteProp<RootStackParamList, 'ChildrenInfoInput'>;

export const ChildrenInfoInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<ChildrenInfoInputScreenRouteProp>();

  const { childrenData, isEditing } = route.params || {};

  // Helper function to format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Format based on length
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    if (cleaned.length <= 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;

    // Limit to 10 digits
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  // Helper function to validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to format date as MM/DD/YYYY
  const formatDate = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Format based on length
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    if (cleaned.length <= 8) return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;

    // Limit to 8 digits (MMDDYYYY)
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
  };

  // Helper function to convert MM/DD/YYYY to ISO 8601 (YYYY-MM-DDTHH:mm:ss.sssZ)
  const convertToISODate = (dateString: string): string => {
    if (!dateString || dateString.length !== 10) return '';

    const [month, day, year] = dateString.split('/');
    if (!month || !day || !year) return '';

    // Create a date object and return in full ISO format
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toISOString();
  };

  // Helper function to convert ISO date to display format (MM/DD/YYYY)
  const convertFromISODate = (isoDate: string): string => {
    if (!isoDate) return '';

    try {
      // Create date object from ISO string
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return '';

      // Extract month, day, year
      const month = date.getMonth() + 1; // getMonth() returns 0-11
      const day = date.getDate();
      const year = date.getFullYear();

      // Return in MM/DD/YYYY format with zero padding
      return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
    } catch (error) {
      return '';
    }
  };

  // Relationship options
  const relationshipTypes = [
    'Son',
    'Daughter',
    'Stepson',
    'Stepdaughter',
    'Adopted Son',
    'Adopted Daughter',
    'Foster Son',
    'Foster Daughter',
    'Other'
  ];

  // Eye color options
  const eyeColors = ['Brown', 'Blue', 'Green', 'Hazel', 'Gray', 'Other'];

  // Hair color options
  const hairColors = ['Brown', 'Blonde', 'Black', 'Red', 'Gray', 'Other'];

  // Helper function to check if all required emails are valid
  const areEmailsValid = (): boolean => {
    const emails = [email];
    return emails.every(emailField => emailField === '' || validateEmail(emailField));
  };

  const [firstName, setFirstName] = useState(childrenData?.firstName || '');
  const [lastName, setLastName] = useState(childrenData?.lastName || '');
  const [relationshipToUser, setRelationshipToUser] = useState(childrenData?.relationshipToUser || '');
  const [dateOfBirth, setDateOfBirth] = useState(
    childrenData?.dateOfBirth ? convertFromISODate(childrenData.dateOfBirth) : ''
  );
  const [feet, setFeet] = useState(childrenData?.feet?.toString() || '');
  const [inches, setInches] = useState(childrenData?.inches?.toString() || '');
  const [weight, setWeight] = useState(childrenData?.weight?.toString() || '');
  const [eyeColor, setEyeColor] = useState(childrenData?.eyeColor || '');
  const [hairColor, setHairColor] = useState(childrenData?.hairColor || '');
  const [identifyingMarks, setIdentifyingMarks] = useState(childrenData?.identifyingMarks || '');
  const [schoolName, setSchoolName] = useState(childrenData?.schoolName || '');
  const [schoolPhone, setSchoolPhone] = useState(childrenData?.schoolPhone || '');
  const [teacherName, setTeacherName] = useState(childrenData?.teacherName || '');
  const [medicalConditions, setMedicalConditions] = useState(childrenData?.medicalConditions || '');
  const [medications, setMedications] = useState(childrenData?.medications || '');
  const [email, setEmail] = useState(childrenData?.email || '');
  const [phoneNumber, setPhoneNumber] = useState(childrenData?.phoneNumber || '');
  const [address, setAddress] = useState(childrenData?.address || '');
  const [saving, setSaving] = useState(false);
  const [showRelationshipDropdown, setShowRelationshipDropdown] = useState(false);
  const [showEyeColorDropdown, setShowEyeColorDropdown] = useState(false);
  const [showHairColorDropdown, setShowHairColorDropdown] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedChildrenId, setSavedChildrenId] = useState<string | undefined>();

  const handleSave = async () => {
    // Validation - require at least first name and last name
    if (!firstName.trim() || !lastName.trim()) {
      Alert.alert('Error', 'Please enter at least first name and last name');
      return;
    }

    // Validate emails before saving
    if (!areEmailsValid()) {
      Alert.alert('Invalid Email', 'Please check that all email addresses are in the correct format (e.g., user@example.com)');
      return;
    }

    try {
      setSaving(true);

      const childrenUpdateData = {
        taskId: 4, // Children task ID
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        relationshipToUser: relationshipToUser.trim(),
        dateOfBirth: dateOfBirth.trim() ? convertToISODate(dateOfBirth.trim()) : undefined,
        feet: feet ? parseInt(feet, 10) : undefined,
        inches: inches ? parseInt(inches, 10) : undefined,
        weight: weight ? parseInt(weight, 10) : undefined,
        eyeColor: eyeColor.trim(),
        hairColor: hairColor.trim(),
        identifyingMarks: identifyingMarks.trim(),
        schoolName: schoolName.trim(),
        schoolPhone: schoolPhone.trim(),
        teacherName: teacherName.trim(),
        medicalConditions: medicalConditions.trim(),
        medications: medications.trim(),
        email: email.trim(),
        phoneNumber: phoneNumber.trim(),
        address: address.trim(),
      };

      let savedChildren;

      if (isEditing && childrenData?.id) {
        // Update existing record
        savedChildren = await childrenInfoService.updateChildrenInfo(
          childrenData.id,
          childrenUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedChildren?.data || savedChildren;
        logger.debug('Children updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('ChildrenInfoLockUnlock', { childrenId: updatedData.id });
      } else {
        // Create new record
        savedChildren = await childrenInfoService.createChildrenInfo(childrenUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedChildren?.data || savedChildren;
        logger.debug('Children saved successfully with ID:', createdData.id);

        // Store the saved ID for the Done button
        setSavedChildrenId(createdData.id);

        // Clear the form and reset states for Add Another
        setFirstName('');
        setLastName('');
        setRelationshipToUser('');
        setDateOfBirth('');
        setFeet('');
        setInches('');
        setWeight('');
        setEyeColor('');
        setHairColor('');
        setIdentifyingMarks('');
        setSchoolName('');
        setSchoolPhone('');
        setTeacherName('');
        setMedicalConditions('');
        setMedications('');
        setEmail('');
        setPhoneNumber('');
        setAddress('');

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset saving so buttons are interactive
        setSaving(false);
      }
    } catch (error) {
      logger.error('Error saving children:', error);
      Alert.alert('Error', 'Failed to save children information. Please try again.');
      setSaving(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFirstName('');
    setLastName('');
    setRelationshipToUser('');
    setDateOfBirth('');
    setFeet('');
    setInches('');
    setWeight('');
    setEyeColor('');
    setHairColor('');
    setIdentifyingMarks('');
    setSchoolName('');
    setSchoolPhone('');
    setTeacherName('');
    setMedicalConditions('');
    setMedications('');
    setEmail('');
    setPhoneNumber('');
    setAddress('');
    setShowAddAnotherDone(false);
    setSaving(false);
  };

  const handleDone = async () => {
    setSaving(false);
    logger.debug('[Done Debug] Using ID:', savedChildrenId);

    // If we don't have a saved ID, get the most recent children entry
    if (!savedChildrenId) {
      try {
        const response = await childrenInfoService.getChildrenInfo(4);
        logger.debug('[Done Debug] API response:', response);

        // Handle response format - check if it's wrapped in a success/data object
        const allChildren = response?.data || response;
        logger.debug('[Done Debug] Extracted children array:', allChildren);

        const mostRecent = allChildren && Array.isArray(allChildren) && allChildren.length > 0 ? allChildren[allChildren.length - 1] : null;
        if (mostRecent) {
          logger.debug('[Done Debug] Using most recent ID:', mostRecent.id);
          navigation.navigate('ChildrenInfoLockUnlock', { childrenId: mostRecent.id });
        } else {
          logger.debug('[Done Debug] No children entries found in array:', allChildren);
          Alert.alert('Error', 'No children information found. Please create an entry first.');
        }
      } catch (error) {
        logger.error('[Done Debug] Error getting children:', error);
        Alert.alert('Error', 'Failed to get children information.');
      }
    } else {
      navigation.navigate('ChildrenInfoLockUnlock', { childrenId: savedChildrenId });
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Children Information
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets={true}
        >
          <View style={styles.inputGroup}>
            <Text style={styles.label}>First Name *</Text>
            <TextInput
              style={styles.input}
              value={firstName}
              onChangeText={setFirstName}
              placeholder="Ex: John"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Last Name *</Text>
            <TextInput
              style={styles.input}
              value={lastName}
              onChangeText={setLastName}
              placeholder="Ex: Smith"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Relationship</Text>
            <TouchableOpacity
              style={styles.dropdownContainer}
              onPress={() => setShowRelationshipDropdown(!showRelationshipDropdown)}
            >
              <Text style={[!relationshipToUser ? styles.dropdownPlaceholder : styles.dropdownText]}>
                {relationshipToUser || 'Select relationship'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownArrow}
              />
            </TouchableOpacity>

            {showRelationshipDropdown && (
              <View style={styles.dropdownList}>
                {relationshipTypes.map((type) => (
                  <TouchableOpacity
                    key={type}
                    style={styles.dropdownItem}
                    onPress={() => {
                      setRelationshipToUser(type);
                      setShowRelationshipDropdown(false);
                    }}
                  >
                    <Text style={styles.dropdownItemText}>{type}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Date of Birth</Text>
            <TextInput
              style={styles.input}
              value={dateOfBirth}
              onChangeText={(text) => setDateOfBirth(formatDate(text))}
              placeholder="MM/DD/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Height (Feet)</Text>
            <TextInput
              style={styles.input}
              value={feet}
              onChangeText={setFeet}
              placeholder="Ex: 5"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Height (Inches)</Text>
            <TextInput
              style={styles.input}
              value={inches}
              onChangeText={setInches}
              placeholder="Ex: 8"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Weight (lbs)</Text>
            <TextInput
              style={styles.input}
              value={weight}
              onChangeText={setWeight}
              placeholder="Ex: 120"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Eye Color</Text>
            <TouchableOpacity
              style={styles.dropdownContainer}
              onPress={() => setShowEyeColorDropdown(!showEyeColorDropdown)}
            >
              <Text style={[!eyeColor ? styles.dropdownPlaceholder : styles.dropdownText]}>
                {eyeColor || 'Select eye color'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownArrow}
              />
            </TouchableOpacity>

            {showEyeColorDropdown && (
              <View style={styles.dropdownList}>
                {eyeColors.map((color) => (
                  <TouchableOpacity
                    key={color}
                    style={styles.dropdownItem}
                    onPress={() => {
                      setEyeColor(color);
                      setShowEyeColorDropdown(false);
                    }}
                  >
                    <Text style={styles.dropdownItemText}>{color}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Hair Color</Text>
            <TouchableOpacity
              style={styles.dropdownContainer}
              onPress={() => setShowHairColorDropdown(!showHairColorDropdown)}
            >
              <Text style={[!hairColor ? styles.dropdownPlaceholder : styles.dropdownText]}>
                {hairColor || 'Select hair color'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownArrow}
              />
            </TouchableOpacity>

            {showHairColorDropdown && (
              <View style={styles.dropdownList}>
                {hairColors.map((color) => (
                  <TouchableOpacity
                    key={color}
                    style={styles.dropdownItem}
                    onPress={() => {
                      setHairColor(color);
                      setShowHairColorDropdown(false);
                    }}
                  >
                    <Text style={styles.dropdownItemText}>{color}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Identifying Marks</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={identifyingMarks}
              onChangeText={setIdentifyingMarks}
              placeholder="Scars, birthmarks, etc."
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>School Name</Text>
            <TextInput
              style={styles.input}
              value={schoolName}
              onChangeText={setSchoolName}
              placeholder="Ex: Lincoln Elementary School"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>School Phone</Text>
            <TextInput
              style={styles.input}
              value={schoolPhone}
              onChangeText={(text) => setSchoolPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Teacher Name</Text>
            <TextInput
              style={styles.input}
              value={teacherName}
              onChangeText={setTeacherName}
              placeholder="Ex: Mrs. Johnson"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Medical Conditions</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={medicalConditions}
              onChangeText={setMedicalConditions}
              placeholder="Any medical conditions or allergies"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Medications</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={medications}
              onChangeText={setMedications}
              placeholder="Current medications"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={[
                styles.input,
                email && !validateEmail(email) && styles.inputError
              ]}
              value={email}
              onChangeText={setEmail}
              placeholder="child@example.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
            {email && !validateEmail(email) && (
              <Text style={styles.errorText}>Please enter a valid email address</Text>
            )}
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number</Text>
            <TextInput
              style={styles.input}
              value={phoneNumber}
              onChangeText={(text) => setPhoneNumber(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Address</Text>
            <TextInput
              style={styles.input}
              value={address}
              onChangeText={setAddress}
              placeholder="123 Main St, City, State ZIP"
              placeholderTextColor="#A9A9A9"
              multiline
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {saving ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSave}
                >
                  <Text style={styles.submitButtonText}>
                    {isEditing ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  dropdownContainer: {
    backgroundColor: '#FFFFFF',
    borderColor: '#d8dbdd',
    borderWidth: ms(1),
    borderRadius: ms(25),
    paddingHorizontal: ms(15),
    height: s(50),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropdownText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  dropdownPlaceholder: {
    fontSize: ms(16, 0.3),
    color: '#A9A9A9',
  },
  dropdownArrow: {
    width: s(24),
    height: s(24),
    tintColor: '#6B7280',
  },
  dropdownList: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    marginTop: ms(5),
    marginBottom: ms(20),
    maxHeight: s(200),
    overflow: 'hidden',
  },
  dropdownItem: {
    padding: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#F3F4F6',
  },
  dropdownItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  inputError: {
    borderColor: '#FF4444',
    borderWidth: ms(2),
  },
  errorText: {
    color: '#FF4444',
    fontSize: ms(14, 0.3),
    marginTop: ms(-10),
    marginBottom: ms(10),
    marginLeft: ms(15),
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
});